import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Wrench, Plus, Trash2, Edit } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  operational: 'bg-success/20 text-success',
  maintenance: 'bg-warning/20 text-warning',
  out_of_service: 'bg-error/20 text-error',
  idle: 'bg-muted text-foreground',
};

export default function MachinesPage() {
  const [machines, setMachines] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: '', model: '', serial_number: '', status: 'operational', location: '', capacity_per_hour: 0, last_maintenance: '', next_maintenance: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try { const res = await api.get('/factory/machines'); setMachines(res.data); } catch { toast.error('Failed'); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      const payload = { ...form, capacity_per_hour: parseFloat(form.capacity_per_hour) || 0 };
      if (editing) { await api.put(`/factory/machines/${editing}`, payload); toast.success('Updated'); }
      else { await api.post('/factory/machines', payload); toast.success('Machine added'); }
      setShowDialog(false); setEditing(null);
      setForm({ name: '', type: '', model: '', serial_number: '', status: 'operational', location: '', capacity_per_hour: 0, last_maintenance: '', next_maintenance: '', notes: '' });
      fetchData();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name || '', type: m.type || '', model: m.model || '', serial_number: m.serial_number || '', status: m.status || 'operational', location: m.location || '', capacity_per_hour: m.capacity_per_hour || 0, last_maintenance: m.last_maintenance || '', next_maintenance: m.next_maintenance || '', notes: m.notes || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete?')) return;
    try { await api.delete(`/factory/machines/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="machines-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Wrench size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="machines-title">Machines & Equipment</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Track machines, maintenance & capacity</p>
            </div>
          </div>
          <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-xl h-9 text-[13px]" data-testid="add-machine-btn"><Plus size={14} className="mr-1.5" />Add Machine</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="machine-dialog">
              <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Edit' : 'Add'} Machine</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="machine-name" /></div>
                  <div><Label className="text-[12px]">Type</Label><Input value={form.type} onChange={e => setForm({...form, type: e.target.value})} placeholder="e.g. CNC, Press, Mold" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Model</Label><Input value={form.model} onChange={e => setForm({...form, model: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Serial Number</Label><Input value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-[12px]">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operational">Operational</SelectItem>
                        <SelectItem value="maintenance">In Maintenance</SelectItem>
                        <SelectItem value="out_of_service">Out of Service</SelectItem>
                        <SelectItem value="idle">Idle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[12px]">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Capacity/hr</Label><Input type="number" value={form.capacity_per_hour} onChange={e => setForm({...form, capacity_per_hour: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[12px]">Last Maintenance</Label><Input type="date" value={form.last_maintenance} onChange={e => setForm({...form, last_maintenance: e.target.value})} /></div>
                  <div><Label className="text-[12px]">Next Maintenance</Label><Input type="date" value={form.next_maintenance} onChange={e => setForm({...form, next_maintenance: e.target.value})} /></div>
                </div>
                <div><Label className="text-[12px]">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
                <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl">{saving ? 'Saving...' : editing ? 'Update' : 'Add Machine'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Total</p><p className="text-2xl font-bold font-outfit text-foreground mt-1">{machines.length}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">Operational</p><p className="text-2xl font-bold font-outfit text-success mt-1">{machines.filter(m => m.status === 'operational').length}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-warning uppercase tracking-[0.08em]">Maintenance</p><p className="text-2xl font-bold font-outfit text-warning mt-1">{machines.filter(m => m.status === 'maintenance').length}</p></CardContent></Card>
          <Card className="stat-card border-0 bg-white"><CardContent className="pt-4 pb-3.5 px-4"><p className="text-[10px] font-semibold text-error uppercase tracking-[0.08em]">Out of Service</p><p className="text-2xl font-bold font-outfit text-error mt-1">{machines.filter(m => m.status === 'out_of_service').length}</p></CardContent></Card>
        </div>

        {/* Machine Cards */}
        {machines.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white"><CardContent className="py-12 text-center text-muted-foreground">
            <Wrench size={32} className="mx-auto mb-3 opacity-30" /><p className="text-[13px]">No machines added</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {machines.map(m => (
              <Card key={m.id} className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow" data-testid="machine-card">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-foreground text-[14px]">{m.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {m.type && <Badge variant="outline" className="text-[10px]">{m.type}</Badge>}
                        <Badge className={`${STATUS_COLORS[m.status] || 'bg-muted'} text-[10px] capitalize`}>{m.status?.replace('_', ' ')}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(m)}><Edit size={13} /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-error" onClick={() => handleDelete(m.id)}><Trash2 size={13} /></Button>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-[12px] text-muted-foreground">
                    {m.model && <div>Model: <span className="font-medium text-foreground">{m.model}</span></div>}
                    {m.serial_number && <div>S/N: <span className="font-mono text-foreground">{m.serial_number}</span></div>}
                    {m.location && <div>Location: {m.location}</div>}
                    {m.capacity_per_hour > 0 && <div>Capacity: <span className="font-semibold text-foreground">{m.capacity_per_hour}/hr</span></div>}
                    {m.next_maintenance && <div className="text-warning">Next maintenance: {m.next_maintenance}</div>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
